import React, { useEffect, useState, useCallback } from "react";
import axios from "../../axios/axios";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { Pagination } from "rsuite";
import { ScaleLoader } from "react-spinners";
import { RootState } from "../../store";
import { selectCanViewTransactions } from "../../store/auth-slice";
import { useDefaultRoute } from "../../hooks/useDefaultRoute";
import { getTokenUserId } from "../../utils/tokenUtils";
import {
  OnlineConsultResponse,
  CONSULT_STATUS_LABELS,
  OnlineConsultPageResponse,
} from "../../types/api";
import { useNotification } from "../../contexts/NotificationContext";
import { fetchConsultReportPdf } from "../../services/onlineConsultService";
import ReportModal from "./ReportModal";
import { formatUtcTime } from "./formatUtcTime";
import styles from "./Transactions.module.scss";
import Calendar from "react-calendar";
import moment from "moment";

const PAGE_SIZE = 20;

// Keep the blob URL alive briefly so the browser can finish reading it into the
// started download before it is revoked.
const REVOKE_BLOB_URL_AFTER_MS = 10_000;

interface ConsultDetailsProps {
  item: OnlineConsultResponse;
}
interface DateFilterModalProps {
  initialDateFrom: string | null;
  initialDateTo: string | null;
  onApply: (from: string, to: string) => void;
  onCancel: () => void;
}

const DateFilterModal: React.FC<DateFilterModalProps> = ({
  initialDateFrom,
  initialDateTo,
  onApply,
  onCancel,
}) => {
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    initialDateFrom
      ? new Date(initialDateFrom)
      : moment().startOf("month").toDate(),
    initialDateTo ? new Date(initialDateTo) : new Date(),
  ]);

  const handleDateChange = (val: unknown) => {
    if (Array.isArray(val) && val.length === 2) {
      setDateRange(val as [Date, Date]);
    }
  };

  const handleApply = () => {
    onApply(
      moment(dateRange[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      moment(dateRange[1]).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.filterModal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Izaberite period</h3>

        <Calendar
          className={styles.filterCalendar}
          selectRange={true}
          returnValue="range"
          value={dateRange}
          onChange={handleDateChange}
          locale="sr"
          maxDate={new Date()}
        />

        <div className={styles.selectedRange}>
          <span>Od: {moment(dateRange[0]).format("DD.MM.YYYY")}</span>
          <span>Do: {moment(dateRange[1]).format("DD.MM.YYYY")}</span>
        </div>

        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
          >
            Otkaži
          </button>
          <button
            type="button"
            className={styles.applyButton}
            onClick={handleApply}
          >
            Primeni
          </button>
        </div>
      </div>
    </div>
  );
};

const ConsultDetails: React.FC<ConsultDetailsProps> = ({ item }) => {
  const { callerInfo, consultantDetails } = item;

  return (
    <div
      className={styles.expandedContent}
      id={`transaction-details-${item.id}`}
    >
      <div className={styles.transactionId}>
        Br. transakcije: <strong>#{item.id}</strong>
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>Konsultacija</h4>
        <div className={styles.row}>
          <span className={styles.label}>Status</span>
          <span
            className={`${styles.value} ${styles[`status_${item.status}`]}`}
          >
            {CONSULT_STATUS_LABELS[item.status]}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Realizovana</span>
          <span className={styles.value}>
            {item.startTimeUtc ? formatUtcTime(item.startTimeUtc) : "-"}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Cena</span>
          <span className={styles.value}>
            {item.free ? "Besplatno" : `${item.price} bodova`}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Trajanje</span>
          <span className={styles.value}>{item.actualDuration ?? "-"}</span>
        </div>
        {item.hasReport && (
          <div className={styles.row}>
            <span className={styles.label}>Izveštaj poslat</span>
            <span className={styles.value}>
              {item.reportCreatedUtc ? formatUtcTime(item.reportCreatedUtc) : "Da"}
            </span>
          </div>
        )}
      </div>

      {consultantDetails && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Farmaceut</h4>
          <div className={styles.row}>
            <span className={styles.label}>Ime</span>
            <span className={styles.value}>
              {consultantDetails.name ?? "-"}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Br. licence</span>
            <span className={styles.value}>
              {consultantDetails.licenseNumber ?? "-"}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Apoteka</span>
            <span className={styles.value}>
              {consultantDetails.marketplace?.name ?? "-"}
            </span>
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>Pacijent</h4>
        <div className={styles.row}>
          <span className={styles.label}>Ime</span>
          <span className={styles.value}>{callerInfo.joinName ?? "-"}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Email</span>
          <span className={styles.value}>{callerInfo.userEmail}</span>
        </div>
      </div>
    </div>
  );
};

const Transactions: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const consultantId = token ? getTokenUserId(token) : null;
  const canViewTransactions = useSelector(selectCanViewTransactions);
  // ITAM-2622: "/order-processing" is a route the consult-only pharmacist may
  // not reach, so the not-allowed redirect goes through the shared helper.
  const defaultRoute = useDefaultRoute();

  const { showNotification } = useNotification();

  const [transactions, setTransactions] = useState<OnlineConsultResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalTransactions, setTotalTransactions] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [reportConsult, setReportConsult] = useState<OnlineConsultResponse | null>(null);
  // A Set, not a single id: downloads on different rows can be in flight at
  // once, and one settling must not clear another row's indicator.
  const [downloadingIds, setDownloadingIds] = useState<Set<number>>(new Set());
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [showCalendar, setShowCalendar] = useState<boolean>(false);

  const loadTransactions = useCallback(
    async (page: number) => {
      if (!consultantId) return;
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get<OnlineConsultPageResponse>(
          "/online_consult",
          {
            params: {
              type: "PHARMACIST_SUPPORT",
              statusIn: "COMPLETED",
              consultantId,
              orderBy: "created:desc",
              pageNum: page,
              pageSize: PAGE_SIZE,
              ...(dateFrom && { from: dateFrom }),
              ...(dateTo && { to: dateTo }),
            },
          },
        );
        setTransactions(response.data.elements);
        setTotalTransactions(response.data.totalNumOfElements);
        setTotalPages(response.data.totalNumOfPages);
      } catch (err) {
        setError("Greška pri učitavanju transakcija. Pokušajte ponovo.");
      } finally {
        setLoading(false);
      }
    },
    [consultantId, dateFrom, dateTo],
  );

  useEffect(() => {
    if (!consultantId || !canViewTransactions) return;
    loadTransactions(currentPage);
  }, [consultantId, canViewTransactions, loadTransactions, currentPage]);

  if (!canViewTransactions) {
    return <Navigate to={defaultRoute} replace />;
  }

  if (!consultantId) {
    return (
      <div className={styles.transactionsPage}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Transakcije</h1>
        </div>
        <div className={styles.errorState}>
          <p>Nije moguće učitati transakcije. Prijavite se ponovo.</p>
        </div>
      </div>
    );
  }

  const handleToggle = (id: number) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const handlePageChange = (page: number) => {
    setOpenId(null);
    setCurrentPage(page - 1);
  };

  const handleReportModalClose = (refetch: boolean) => {
    setReportConsult(null);
    if (refetch) {
      loadTransactions(currentPage);
    }
  };

  const handleDownloadPdf = async (consultId: number) => {
    setDownloadingIds((prev) => new Set(prev).add(consultId));
    try {
      const blob = await fetchConsultReportPdf(consultId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `izvestaj-${consultId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_BLOB_URL_AFTER_MS);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        // hasReport was stale — refresh so the row falls back to "write".
        showNotification("error", "Izveštaj nije pronađen.");
        loadTransactions(currentPage);
      } else {
        showNotification("error", "Greška pri preuzimanju izveštaja. Pokušajte ponovo.");
      }
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(consultId);
        return next;
      });
    }
  };

  const handleApplyDateFilter = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    setCurrentPage(0);
    setShowCalendar(false);
  };

  const handleClearDateFilter = () => {
    setDateFrom(null);
    setDateTo(null);
    setCurrentPage(0);
    setOpenId(null);
  };

  return (
    <div className={styles.transactionsPage}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Transakcije</h1>
        <button
          type="button"
          className={styles.periodButton}
          onClick={() => setShowCalendar(true)}
        >
          {dateFrom && dateTo
            ? `${moment(dateFrom).format("DD.MM.YYYY")} - ${moment(dateTo).format("DD.MM.YYYY")}`
            : "Izaberi period"}
        </button>
        {dateFrom && dateTo && (
          <button
            type="button"
            className={styles.clearPeriodButton}
            onClick={handleClearDateFilter}
          >
            Poništi period
          </button>
        )}
      </div>

      {showCalendar && (
        <DateFilterModal
          initialDateFrom={dateFrom}
          initialDateTo={dateTo}
          onApply={handleApplyDateFilter}
          onCancel={() => setShowCalendar(false)}
        />
      )}

      {loading && (
        <div className={styles.loadingState}>
          <p>Učitavanje...</p>
        </div>
      )}

      {error && !loading && (
        <div className={styles.errorState}>
          <p>{error}</p>
          <button onClick={() => loadTransactions(currentPage)}>
            Pokušaj ponovo
          </button>
        </div>
      )}

      {!loading && !error && transactions.length === 0 && (
        <div className={styles.emptyState}>
          <p>Nema transakcija.</p>
        </div>
      )}

      {!loading && !error && transactions.length > 0 && (
        <>
          <ul className={styles.transactionsList}>
            {transactions.map((item) => {
              const isOpen = openId === item.id;
              return (
                <li key={item.id} className={styles.transactionItem}>
                  <div className={`${styles.summaryRow} ${isOpen ? styles.isOpen : ""}`}>
                    <button
                      type="button"
                      className={styles.transactionSummary}
                      onClick={() => handleToggle(item.id)}
                      aria-expanded={isOpen}
                      aria-controls={`transaction-details-${item.id}`}
                    >
                      <div className={styles.summaryLeft}>
                        <span className={styles.transactionDate}>
                          {formatUtcTime(item.createdTime)}
                        </span>
                        <span className={styles.transactionSubtitle}>
                          Farmaceutska konsultacija
                        </span>
                      </div>
                      <div className={styles.summaryRight}>
                        <span
                          className={`${styles.expandIcon} ${isOpen ? styles.rotated : ""}`}
                        >
                          ▾
                        </span>
                      </div>
                    </button>

                    {/* hasReport is only returned by backends with ITAM-2601 */}
                    {item.hasReport !== undefined && (
                      <div className={styles.reportActions}>
                        {item.hasReport ? (
                          <button
                            type="button"
                            className={`${styles.reportButton} ${styles.reportButtonSecondary}`}
                            onClick={() => handleDownloadPdf(item.id)}
                            disabled={downloadingIds.has(item.id)}
                            aria-label={`Preuzmi PDF izveštaja za transakciju #${item.id}`}
                          >
                            {downloadingIds.has(item.id) ? (
                              <ScaleLoader color="#00b2b1" height={10} width={2} />
                            ) : (
                              "Preuzmi PDF"
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={styles.reportButton}
                            onClick={() => setReportConsult(item)}
                            aria-label={`Napiši izveštaj za transakciju #${item.id}`}
                          >
                            Napiši izveštaj
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {isOpen && <ConsultDetails item={item} />}
                </li>
              );
            })}
          </ul>
          <div className={styles.pageFooter}>
            {totalPages > 1 && (
              <Pagination
                total={totalTransactions}
                limit={PAGE_SIZE}
                activePage={currentPage + 1}
                onChangePage={handlePageChange}
                prev
                next
                maxButtons={7}
                ellipsis
                boundaryLinks
              />
            )}
            <span>{totalTransactions} transakcija</span>
          </div>
        </>
      )}

      {reportConsult && (
        <ReportModal consult={reportConsult} onClose={handleReportModalClose} />
      )}

    </div>
  );
};

export default Transactions;
